import { ComponentPropsWithoutRef, ReactNode, createContext, useContext, useMemo } from 'react';
import { Label } from '@givebutter/primitives/label';
import {
    CampaignDescriptionValues,
    ContentType,
} from 'assets/js/pages/campaign-settings/campaign-details/types';
import * as Form from 'components/form';
import * as Wysiwyg from 'components/wysiwyg';
import { UseFormWysiwyg } from 'components/wysiwyg/UseFormWysiwyg';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { twMerge } from 'tailwind-merge';

const EDITOR_MIN_HEIGHT = 400;

interface CampaignDescriptionFormProps {
    children: ReactNode;
}

interface CampaignDescriptionFormContext {
    isDescription: boolean;
    contentType: 'description' | 'story';
}

const CampaignDescriptionFormContext = createContext<CampaignDescriptionFormContext | null>(null);

const useCampaignDescriptionForm = () => {
    const context = useContext(CampaignDescriptionFormContext);
    if (!context) {
        throw new Error(
            'useCampaignDescriptionForm must be used within a CampaignDescriptionForm provider',
        );
    }
    return context;
};

const CampaignDescriptionForm = ({
    children,
    ...props
}: CampaignDescriptionFormProps & React.HTMLAttributes<HTMLDivElement>) => {
    const { data: campaign } = useCurrentCampaign();
    const isDescription = campaign.type === 'collect';
    const contentType = isDescription ? 'description' : ('story' as ContentType);

    const contextValue = useMemo(
        () => ({
            isDescription,
            contentType,
        }),
        [isDescription, contentType],
    );

    return (
        <CampaignDescriptionFormContext.Provider value={contextValue}>
            <div {...props} className="flex w-full flex-col gap-4">
                {children}
            </div>
        </CampaignDescriptionFormContext.Provider>
    );
};

interface CampaignDescriptionFormHeaderProps extends ComponentPropsWithoutRef<'div'> {}
const CampaignDescriptionFormHeader = ({
    children,
    className,
    ...props
}: CampaignDescriptionFormHeaderProps) => (
    <div className={twMerge('flex w-full items-start justify-between', className)} {...props}>
        {children}
    </div>
);

interface CampaignDescriptionFormContainerProps extends ComponentPropsWithoutRef<'div'> {}
const CampaignDescriptionFormContainer = ({
    children,
    className,
    ...props
}: CampaignDescriptionFormContainerProps) => (
    <div className={twMerge('flex max-w-[20rem] flex-col', className)} {...props}>
        {children}
    </div>
);

interface CampaignDescriptionFormTitleProps extends ComponentPropsWithoutRef<'p'> {}
const CampaignDescriptionFormTitle = ({
    className,
    ...props
}: CampaignDescriptionFormTitleProps) => {
    const { isDescription } = useCampaignDescriptionForm();
    const title = isDescription ? 'Description' : 'Story';

    return (
        <p className={twMerge('text-lg font-medium', className)} {...props}>
            {title}
        </p>
    );
};

interface CampaignDescriptionFormDescriptionProps extends ComponentPropsWithoutRef<'p'> {}
const CampaignDescriptionFormDescription = ({
    className,
    ...props
}: CampaignDescriptionFormDescriptionProps) => {
    const { contentType } = useCampaignDescriptionForm();
    const description = `Add a ${contentType} to your campaign. This appears directly below the title.`;

    return (
        <p className={twMerge('text-sm text-gray-700', className)} {...props}>
            {description}
        </p>
    );
};

interface CampaignDescriptionFormSubmitProps
    extends ComponentPropsWithoutRef<typeof Form.SubmitButton> {}
const CampaignDescriptionFormSubmit = ({
    children = 'Save',
    ...props
}: CampaignDescriptionFormSubmitProps) => (
    <Form.SubmitButton variant="solid" {...props}>
        {children}
    </Form.SubmitButton>
);

interface CampaignDescriptionFormEditorProps extends ComponentPropsWithoutRef<'div'> {}
const CampaignDescriptionFormEditor = ({
    className,
    ...props
}: CampaignDescriptionFormEditorProps) => {
    const { isBeta } = useCurrentAccount();
    const aiEnabled = isBeta;
    const { isDescription, contentType } = useCampaignDescriptionForm();
    const { control, path } = Form.useGivebutterFormContext<CampaignDescriptionValues>();

    const editorId = isDescription ? 'campaign_description' : 'campaign_story';
    const editorPlaceholder = !isDescription ? 'Tell your story...' : undefined;

    const wysiwygConfig = isDescription
        ? {
              min_height: EDITOR_MIN_HEIGHT,
              statusbar: false,
              placeholder: 'Enter a description...',
              plugins: [
                  ...(aiEnabled ? ['ai'] : []),
                  ...Wysiwyg.Constants.DESCRIPTION_EDITOR_PLUGINS,
                  'autoresize',
              ],
              toolbar: `aidialog aishortcuts ${Wysiwyg.Constants.DESCRIPTION_EDITOR_TOOLBAR}`,
              content_style: Wysiwyg.Constants.APP_FONT,
              menubar: false,
              link_target_list: false,
              link_assume_external_targets: true,
          }
        : {
              min_height: EDITOR_MIN_HEIGHT,
              statusbar: false,
              plugins: [
                  ...(aiEnabled ? ['ai'] : []),
                  ...Wysiwyg.Constants.MINI_EDITOR_PLUGINS,
                  'autoresize',
                  'anchor',
              ],
              toolbar: `aidialog aishortcuts fontsize align ${Wysiwyg.Constants.MINI_EDITOR_TOOLBAR} anchor code`,
              content_style: Wysiwyg.Constants.APP_FONT,
              menubar: false,
          };

    return (
        <div className={twMerge(className)} {...props}>
            <Label htmlFor={editorId} className="sr-only">
                Campaign {contentType}
            </Label>
            <Wysiwyg.Root>
                <UseFormWysiwyg
                    id={editorId}
                    name={path('description')}
                    control={control}
                    inline={false}
                    init={{
                        placeholder: editorPlaceholder,
                        width: '100%',
                        ...wysiwygConfig,
                    }}
                />
            </Wysiwyg.Root>
        </div>
    );
};

const Root = CampaignDescriptionForm;
const Header = CampaignDescriptionFormHeader;
const Container = CampaignDescriptionFormContainer;
const Title = CampaignDescriptionFormTitle;
const Description = CampaignDescriptionFormDescription;
const Submit = CampaignDescriptionFormSubmit;
const Editor = CampaignDescriptionFormEditor;

export {
    Root,
    Header,
    Container,
    Title,
    Description,
    Submit,
    Editor,
    //
    CampaignDescriptionForm,
    CampaignDescriptionFormHeader,
    CampaignDescriptionFormContainer,
    CampaignDescriptionFormTitle,
    CampaignDescriptionFormDescription,
    CampaignDescriptionFormSubmit,
    CampaignDescriptionFormEditor,
};
